import {ChangeDetectionStrategy, Component, Input, OnInit} from "@angular/core";
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {combineLatest, Observable, of, startWith} from "rxjs";
import {FeeType, StandardName} from "../../../../../store/state/order.state";
import {PriceAdjustmentsFormService} from "../price-adjustments-form.service";
import {PriceAdjustmentsConfig} from "../pricing-adjustments.component";
import {filter, map, switchMap} from "rxjs/operators";
import {FeeConfig, FeeMode} from "../../../../../configuration/configuration.model";
import {Actions, ofType} from "@ngrx/effects";
import {OrderActions} from "../../../../../store/actions/order.actions";
import {NbInputModule, NbRadioModule} from "@nebular/theme";
import {AsyncPipe} from "@angular/common";

@Component({
	selector: 'direct-price-adjustments',
	templateUrl: './direct-price-adjustments.template.html',
	styleUrls: ['./direct-price-adjustments.style.scss'],
	imports: [
		NbRadioModule,
		ReactiveFormsModule,
		AsyncPipe,
		NbInputModule
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DirectPriceAdjustmentsComponent implements OnInit {
	@Input() form: FormGroup;
	@Input() feesRateHint$: Observable<string>;
	@Input() dailyRateControlName: string;
	@Input() feesModeControlName: string;
	@Input() standard: StandardName;
	@Input() config$!: Observable<PriceAdjustmentsConfig>;
	@Input() formService!: PriceAdjustmentsFormService;
	@Input() preAuditRequested!: boolean;
	@Input() isTransfer: boolean = false;

	fees$: Observable<FeeControl[]>;
	showDailyRate: Observable<boolean> = of(true);

	constructor(private actions$: Actions) { }

	ngOnInit(): void {
		const feeModeControl = this.feeControlName('fee-modes');
		const mode$ = this.form.get(feeModeControl).valueChanges.pipe(
			startWith(this.form.get(feeModeControl)!.value as FeeMode)
		);

		const preAuditRequested$ = this.actions$.pipe(
			ofType(OrderActions.SET_PRE_AUDIT_REQUESTED),
			filter(action => action.standardName === this.standard),
			map(action => action.value),
			startWith(this.preAuditRequested)
		);

		this.fees$ = combineLatest([mode$, preAuditRequested$]).pipe(
			switchMap(([mode, preAuditRequested]) => this.prepareFees(mode, preAuditRequested))
		);

		this.showDailyRate = mode$.pipe(
			map(mode => mode === FeeMode.PER_YEAR)
		);
	}

	prepareFees(mode: FeeMode, preAuditRequested: boolean): Observable<FeeControl[]> {
		return this.config$.pipe(
			map(config => {
				return config.fees
					.filter(fee => fee.mode == mode)
					.map(fee => {
						return ({
							controlName: this.feeControlName(fee.type),
							shouldShow: this.getShouldShow(fee, mode, preAuditRequested),
							label: fee.label,
							placeholder: fee.placeholder
						});
					});
			})
		);
	}

	private getShouldShow(fee: FeeConfig, mode: FeeMode, preAuditRequested: boolean) {
		if ((fee.type === FeeType.PRE_AUDIT_FEE && !preAuditRequested)) return false;
		if (this.isTransfer && fee.excludeForTransfer) return false;
		if (!this.isTransfer && fee.type == FeeType.RECERTIFICATION_DAILY_RATE) return false;
		return !fee.excludeForProducts.includes(this.standard) && fee.mode == mode;
	}

	feeControlName(feeName: string): string {
		return this.formService.feeFormControl(this.standard, feeName);
	}

	isInvalid(controlName: string): boolean {
		return this.formService.isInvalid(this.standard, controlName);
	}

	getValidationErrors(controlName: string): Observable<string> {
		return this.formService.getErrorsString(this.standard, controlName);
	}

	preventNonNumericalInput(e): void {
		const pattern = /[0-9]/;
		const inputChar = String.fromCharCode(e.charCode);
		if (!pattern.test(inputChar)) {
			e.preventDefault();
		}
	}
}

interface FeeControl {
	controlName: string;
	shouldShow: boolean;
	label: string;
	placeholder: string;
}
